import { useCallback, useState } from 'react'
import {
  Control,
  FieldErrors,
  UseFormHandleSubmit,
  UseFormSetError,
} from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import { LoginFormValues } from '@/pages/Login/Login'
import { useCartStore } from '@/stores/cart/cartStore'
import { useFeedStore } from '@/stores/feed/feedStore'
import { useSearchStore } from '@/stores/feed/searchStore'
import { publicApi, setMemoryToken } from '@/utils/api/api'
import getGeneralErrorMessage from '@/utils/api/errors/getGeneralErrorMessage'
import { ensureWebAccess, WebAccessDeniedError } from '@/utils/auth/webAccess'
import { denormalizePhoneForBackend } from '@/utils/phone/denormalizePhoneForBackend'

import cn from '../Forms.module.scss'
import { PhoneInput } from '../PhoneInput/PhoneInput'
import { SMSCodeInput } from '../SMSCodeInput/SMSCodeInput'

type ByPhoneFormPropsType = {
  control: Control<LoginFormValues>
  errors: FieldErrors<LoginFormValues>
  handleSubmit: UseFormHandleSubmit<LoginFormValues>
  setError: UseFormSetError<LoginFormValues>
}

function ByPhoneForm({
  control,
  errors,
  handleSubmit,
  setError,
}: ByPhoneFormPropsType) {
  const [enableCodeInput, setEnable] = useState<boolean>(false)
  const [isSuccessAnimating, setIsSuccessAnimating] = useState(false)
  const [formattedPhone, setFormattedPhone] = useState('')

  const navigate = useNavigate()

  const verifyCode = useCallback(
    async (code: string) => {
      setIsSuccessAnimating(true)
      publicApi
        .post(
          `/sms/verify-and-auth`,
          {
            phoneNumber: formattedPhone,
            verificationCode: code,
          },
          { _skipAuth: true }
        )
        .then(ensureWebAccess)
        .then((response) => {
          localStorage.removeItem('currentClientId')
          localStorage.removeItem('selectedOrganisationCode')
          localStorage.removeItem('cart-storage')

          useSearchStore.getState().setInitialSearchStore()
          useFeedStore.getState().setInitialFeedStore()
          useCartStore.getState().setInitialCartStore()

          const { accessToken, refreshToken } = response.data.token
          setMemoryToken(accessToken)
          localStorage.setItem('refresh-token', refreshToken)
          navigate('/', { replace: true })
        })
        .catch((error) => {
          setIsSuccessAnimating(false)
          const serverMessage =
            error instanceof WebAccessDeniedError
              ? error.message
              : error.response?.data?.message || getGeneralErrorMessage(error)
          setError('phone', { type: 'manual', message: serverMessage })
          toast.error(serverMessage)
        })
    },
    [formattedPhone, navigate, setError]
  )

  const sendSMS = (data: LoginFormValues) => {
    const digitsOnly = denormalizePhoneForBackend(data.phone)
    setFormattedPhone(digitsOnly)

    toast.promise(publicApi.post(`/sms/send`, { phoneNumber: digitsOnly }), {
      loading: 'Отправка SMS...',
      success: () => {
        setEnable(true)
        return 'Сообщение успешно отправлено'
      },
      error: (error) => {
        const serverMessage =
          error.response?.data?.message || getGeneralErrorMessage(error)
        setError('phone', { type: 'manual', message: serverMessage })
        return serverMessage
      },
    })
  }

  return (
    <div className={cn.formBlock}>
      <form onSubmit={handleSubmit(sendSMS)}>
        <div className={cn.inputs}>
          <div className={cn.inputWrapper}>
            <label className={cn.label} htmlFor="login-sms-phone">
              Телефон
            </label>
            <PhoneInput
              id="login-sms-phone"
              name="phone"
              control={control}
              errors={errors}
            />
            {errors.phone && (
              <p className={cn.errorMessage}>{errors.phone.message}</p>
            )}
          </div>
          {enableCodeInput && (
            <SMSCodeInput
              onComplete={verifyCode}
              isSuccess={isSuccessAnimating}
            />
          )}
        </div>
        {!enableCodeInput ? (
          <AtomButton fullWidth htmlType="submit">
            Отправить СМС с кодом
          </AtomButton>
        ) : (
          <div className={cn.codeHint}>Введите 4-значный код из СМС</div>
        )}
      </form>
    </div>
  )
}

export default ByPhoneForm
